import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ROUTES,
  CANDIDATE_ROLE_KEY,
  CANDIDATE_REDIRECT_KEY,
} from '../../constants';

/**
 * Routes a candidate is allowed to visit.
 * Everything else redirects to the stored job apply URL.
 */
const CANDIDATE_ALLOWED_PATTERNS: RegExp[] = [
  // Candidate auth pages
  new RegExp(`^${ROUTES.CANDIDATE_SIGNIN}$`),
  new RegExp(`^${ROUTES.CANDIDATE_SIGNUP}$`),
  new RegExp(`^${ROUTES.CANDIDATE_FORGOT_PASSWORD}$`),
  new RegExp(`^${ROUTES.OAUTH_REDIRECT}$`),
  // Job view & apply: /job/:jobId (2 seg) and /job/:jobId/apply (3 seg)
  /^\/job\/[^/]+$/,
  /^\/job\/[^/]+\/apply$/,
  // Legacy: /job/:orgId/:jobId and /job/:orgId/:jobId/apply
  /^\/job\/[^/]+\/[^/]+$/,
  /^\/job\/[^/]+\/[^/]+\/apply$/,
  // Candidate interview flow
  /^\/interview\/[^/]+$/,
];

const isAllowedForCandidate = (pathname: string): boolean =>
  CANDIDATE_ALLOWED_PATTERNS.some((pattern) => pattern.test(pathname));

interface CandidateRouteGuardProps {
  children: React.ReactNode;
}

/**
 * Match any job route and return the apply variant.
 * Supports: /job/:jobId, /job/:jobId/apply, /job/:orgId/:jobId, /job/:orgId/:jobId/apply.
 */
const toApplyRoute = (pathname: string): string | null => {
  if (pathname.endsWith('/apply')) return pathname;
  const twoSeg = pathname.match(/^\/job\/([^/]+)\/([^/]+)$/);
  if (twoSeg) return `/job/${twoSeg[1]}/${twoSeg[2]}/apply`;
  const oneSeg = pathname.match(/^\/job\/([^/]+)$/);
  if (oneSeg) return `/job/${oneSeg[1]}/apply`;
  return null;
};

/**
 * If the current user is a candidate and the given pathname is a job route,
 * persist the apply-variant URL in localStorage.
 */
const syncJobRedirect = (pathname: string): void => {
  const role = localStorage.getItem(CANDIDATE_ROLE_KEY);
  if (role !== 'CANDIDATE') return;
  const applyRoute = toApplyRoute(pathname);
  if (applyRoute) {
    localStorage.setItem(CANDIDATE_REDIRECT_KEY, applyRoute);
  }
};

/**
 * Wraps the entire route tree.
 * - Keeps CANDIDATE_REDIRECT_KEY in localStorage up-to-date whenever the
 *   candidate visits any job-related route (view or apply).
 * - Redirects candidates away from non-candidate routes to the stored job URL.
 */
const CandidateRouteGuard: React.FC<CandidateRouteGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const role = localStorage.getItem(CANDIDATE_ROLE_KEY);

  // 1. Sync update on every React render (covers client-side navigation & hard reloads)
  syncJobRedirect(location.pathname);

  // 2. Listen to browser back / forward (popstate) at the DOM level so
  //    localStorage is updated even before React re-renders.
  const handlePopState = useCallback(() => {
    syncJobRedirect(window.location.pathname);
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  // 3. Also run on every location change via useEffect as a safety net
  useEffect(() => {
    syncJobRedirect(location.pathname);
  }, [location.pathname]);

  // 4. Redirect candidates away from non-allowed routes
  useEffect(() => {
    if (role !== 'CANDIDATE') return;

    if (!isAllowedForCandidate(location.pathname)) {
      const storedRedirect = localStorage.getItem(CANDIDATE_REDIRECT_KEY);
      if (storedRedirect) {
        navigate(storedRedirect, { replace: true });
      } else {
        navigate(ROUTES.CANDIDATE_SIGNIN, { replace: true });
      }
    }
  }, [role, location.pathname, navigate]);

  return <>{children}</>;
};

export default CandidateRouteGuard;
